import type { Building } from "building-types/building/buildingTypes";
import type { GeneratedModifier } from "building-types/modifiers/generatedModifier";
import type { Modifier } from "building-types/modifiers/modifierTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { RWTable, Table } from "common/data/table";
import {
  ARCHETYPE_INSTANCE_CHAIN_KEY,
  composeComponentInstanceChain,
  EMPTY_INSTANCE_CHAIN,
} from "doc/entity/componentInstanceEntityResolve";
import {
  evaluateEntityForComponentInstance,
  recursivelyDisableByGroup,
  resolveOptionsInFormula,
} from "doc/formula/evalFormulas";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  doc: { modifiers, optionSets, instanceOptionSets },
  optionChoices,
  entityDisabledByGroup,
  componentToTransforms,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    modifiers,
    definitions,
    optionChoices: resolveOptionsInFormula(modifiers, definitions, optionChoices),
    entityDisabledByGroup,
    componentToTransforms,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  modifiers: "deep",
  definitions: "shallow",
  componentToTransforms: "shallow",
});

/**
 * Resolves each modifier's `disabled` formula against one component instance chain's option choices
 * and keeps the enabled ones. `modifiers` must already carry the group disable.
 */
function modifiersForInstanceChain(
  modifiers: Table<Modifier>,
  componentInstanceChain: readonly string[],
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
): Table<GeneratedModifier> {
  const result: RWTable<GeneratedModifier> = {};
  for (const id in modifiers) {
    const evaluated = evaluateEntityForComponentInstance(
      modifiers[id],
      componentInstanceChain,
      definitions,
      optionChoices,
    );
    if (evaluated.disabled) {
      continue;
    }
    result[id] = { moveX: evaluated.moveX ?? 0, moveY: evaluated.moveY ?? 0 };
  }
  return result;
}

function generate({
  modifiers,
  definitions,
  optionChoices,
  entityDisabledByGroup,
  componentToTransforms,
}: ReturnType<typeof select>): {
  modifiers: Table<GeneratedModifier>;
  modifiersByInstanceChain: Table<Table<GeneratedModifier>>;
} {
  const modifiersByInstanceChain: RWTable<Table<GeneratedModifier>> = {};
  if (!modifiers) {
    return { modifiers: {}, modifiersByInstanceChain };
  }

  // Marks modifiers in a disabled group as disabled; `evaluateEntityForComponentInstance` does not
  // check groups itself.
  const groupAwareModifiers: Table<Modifier> = recursivelyDisableByGroup(modifiers, entityDisabledByGroup);

  // The empty chain is the archetype world: no instance option set applies, so every choice cascades
  // up to its archetype.
  const chains: (readonly string[])[] = [EMPTY_INSTANCE_CHAIN];
  for (const componentId in componentToTransforms) {
    for (const transform of componentToTransforms[componentId]) {
      chains.push(transform.componentInstanceChain);
    }
  }

  for (const chain of chains) {
    const chainKey = composeComponentInstanceChain(chain);
    if (modifiersByInstanceChain[chainKey]) {
      continue;
    }
    modifiersByInstanceChain[chainKey] = modifiersForInstanceChain(
      groupAwareModifiers,
      chain,
      definitions,
      optionChoices,
    );
  }

  return { modifiers: modifiersByInstanceChain[ARCHETYPE_INSTANCE_CHAIN_KEY], modifiersByInstanceChain };
}

export default memo(select, generate, compare);
